import React, { useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { usePortal } from '../context/PortalContext';
import { clubService } from '../services/clubService';
import { ClubMember } from '../types';
import { formatDateTime, getMemberRoleLabel } from '../utils/formatters';

const getMemberStatusLabel = (value: ClubMember['status']): string => {
  switch (value) {
    case 'ACTIVE':
      return 'Ativo';
    case 'INACTIVE':
      return 'Inativo';
    case 'BANNED':
      return 'Banido';
    default:
      return value;
  }
};

const getMemberStatusTone = (value: ClubMember['status']): string => {
  switch (value) {
    case 'ACTIVE':
      return 'portal-status-pill--positive';
    case 'INACTIVE':
      return 'portal-status-pill--warning';
    case 'BANNED':
      return 'portal-status-pill--danger';
    default:
      return 'portal-status-pill--neutral';
  }
};

const getMemberRolePillClass = (value: ClubMember['role']): string => {
  switch (value) {
    case 'OWNER':
      return 'portal-role-pill portal-role-pill--owner';
    case 'ADMIN':
      return 'portal-role-pill portal-role-pill--admin';
    case 'MOD':
      return 'portal-role-pill portal-role-pill--mod';
    case 'MEMBER':
    default:
      return 'portal-role-pill portal-role-pill--member';
  }
};

const MembersPage: React.FC = () => {
  const { selectedClub } = usePortal();
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!selectedClub?.id) {
      setMembers([]);
      return;
    }

    setLoading(true);
    setError('');

    clubService
      .getClubMembers(selectedClub.id)
      .then(setMembers)
      .catch((nextError: any) => setError(nextError.message || 'Erro ao carregar membros'))
      .finally(() => setLoading(false));
  }, [selectedClub?.id]);

  const summary = useMemo(() => {
    return members.reduce(
      (accumulator, member) => {
        accumulator.total += 1;
        accumulator[member.status.toLowerCase() as 'active' | 'inactive' | 'banned'] += 1;
        if (member.role === 'OWNER' || member.role === 'ADMIN' || member.role === 'MOD') {
          accumulator.governance += 1;
        }
        return accumulator;
      },
      {
        total: 0,
        active: 0,
        inactive: 0,
        banned: 0,
        governance: 0,
      }
    );
  }, [members]);

  return (
    <div className="portal-page-grid portal-page-stack">
      <section className="portal-section-card portal-section-card--hero portal-section-card--dashboard-hero">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Governanca do club</p>
            <h3>Membros</h3>
          </div>
          <div className="portal-section-card__header-actions">
            <span className="portal-chip">{selectedClub?.code || 'Club'}</span>
            <span className="portal-chip">{summary.total} membro(s)</span>
          </div>
        </div>

        <p className="portal-muted">
          Visao direta da governanca do club, com papeis, situacao operacional e data de entrada de cada membro.
        </p>

        <div className="portal-dashboard-hero-stats">
          <article className="portal-dashboard-hero-stats__item">
            <span>Ativos</span>
            <strong>{summary.active}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Inativos</span>
            <strong>{summary.inactive}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Banidos</span>
            <strong>{summary.banned}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Governanca</span>
            <strong>{summary.governance}</strong>
          </article>
        </div>
      </section>

      <section className="portal-metrics-grid portal-dashboard-metrics-grid">
        <MetricCard
          label="Membros totais"
          value={String(summary.total)}
          helper="Todos os membros retornados pelo backend"
          variant="dashboard"
          theme="balance"
          badge="Total"
        />
        <MetricCard
          label="Ativos"
          value={String(summary.active)}
          helper="Prontos para operar no club"
          variant="dashboard"
          theme="receivable"
          badge="Status"
        />
        <MetricCard
          label="Inativos"
          value={String(summary.inactive)}
          helper="Sem operacao ativa no momento"
          variant="dashboard"
          theme="warning"
          badge="Status"
        />
        <MetricCard
          label="Governanca"
          value={String(summary.governance)}
          helper="Owner, admins e moderadores"
          variant="dashboard"
          theme="rake"
          badge="Acesso"
        />
      </section>

      {error ? <div className="portal-banner portal-banner--error">{error}</div> : null}

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-balance">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Base do club</p>
            <h3>Lista de membros</h3>
          </div>
          <div className="portal-section-card__header-actions">
            <span className="portal-chip">{summary.governance} na governanca</span>
            <span className="portal-chip">{summary.active} ativos</span>
          </div>
        </div>

        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Papel</th>
                <th>Status</th>
                <th>Entrada</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4}>Carregando membros...</td>
                </tr>
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={4}>Nenhum membro retornado.</td>
                </tr>
              ) : (
                members.map((member, index) => (
                  <tr key={`${member.userId || 'member'}-${member.joinedAt || 'no-date'}-${member.role}-${index}`}>
                    <td>
                      <div className="portal-table__cell-stack">
                        <strong>{member.userName}</strong>
                        <span>{member.userId}</span>
                      </div>
                    </td>
                    <td>
                      <span className={getMemberRolePillClass(member.role)}>{getMemberRoleLabel(member.role)}</span>
                    </td>
                    <td>
                      <span className={`portal-status-pill ${getMemberStatusTone(member.status)}`}>
                        {getMemberStatusLabel(member.status)}
                      </span>
                    </td>
                    <td>{formatDateTime(member.joinedAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default MembersPage;
